import { buildLegacyFileUrl, getPresignedFileUrl } from '../services/fileUrlService';
import { isInlineFileSrc, isLocalAttachmentKey, readLocalFile } from '../services/offlineAttachmentStore';
import JSZip from 'jszip';

export const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
};

export const formatAudioDuration = (seconds) => {
  if (seconds == null || seconds === '') return '';
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '';
  const total = Math.round(value);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  }
  return `${minutes}:${String(secs).padStart(2, '0')}`;
};

/** MediaRecorder WebM often reports duration as Infinity/NaN until you seek to the end. */
export const probeAudioElementDuration = (audioEl) => {
  if (!audioEl) return false;
  const duration = audioEl.duration;
  return Number.isFinite(duration) && duration > 0 ? duration : null;
};

export const readBlobDurationSeconds = (blob) => {
  if (!(blob instanceof Blob) || !blob.size || typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  const viaWebAudio = async () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    const ctx = new AudioCtx();
    try {
      const buffer = await blob.arrayBuffer();
      const decoded = await ctx.decodeAudioData(buffer.slice(0));
      const duration = decoded?.duration;
      return Number.isFinite(duration) && duration > 0 ? duration : null;
    } finally {
      if (ctx && ctx.state !== 'closed') {
        try {
          const pending = ctx.close();
          if (pending && typeof pending.catch === 'function') pending.catch(() => {});
        } catch {
          // ignore
        }
      }
    }
  };

  const viaElement = () => new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const audio = document.createElement('audio');
    audio.preload = 'metadata';
    let settled = false;
    const finish = (value) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      audio.ontimeupdate = null;
      audio.onloadedmetadata = null;
      audio.onerror = null;
      audio.removeAttribute('src');
      audio.load();
      URL.revokeObjectURL(url);
      resolve(value);
    };
    const timeoutId = window.setTimeout(() => finish(null), 4000);
    audio.onerror = () => finish(null);
    audio.onloadedmetadata = () => {
      const ready = probeAudioElementDuration(audio);
      if (ready) {
        finish(ready);
        return;
      }
      audio.ontimeupdate = () => {
        const probed = probeAudioElementDuration(audio);
        if (probed) finish(probed);
      };
      try {
        audio.currentTime = 1e101;
      } catch {
        finish(null);
      }
    };
    audio.src = url;
  });

  return viaWebAudio().catch(() => null).then((decoded) => {
    if (Number.isFinite(decoded) && decoded >= 0.5) return decoded;
    return viaElement().then((probed) => (
      Number.isFinite(probed) && probed >= 0.5 ? probed : (decoded || null)
    ));
  });
};

export const readUrlDurationSeconds = async (url) => {
  if (!url || typeof window === 'undefined') return null;
  try {
    const response = await fetch(url);
    if (!response.ok) return null;
    const blob = await response.blob();
    return readBlobDurationSeconds(blob);
  } catch {
    return null;
  }
};

export const parseVoiceNoteDurationSeconds = (fileName) => {
  const match = String(fileName || '').match(/voice-note-\d+-(\d+(?:\.\d+)?)s\./i);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
};

export const isRecordedVoiceNote = (attachmentOrName) => {
  const name = typeof attachmentOrName === 'string'
    ? attachmentOrName
    : (attachmentOrName?.fileName || attachmentOrName?.originalFileName || '');
  return /^voice-note-/i.test(name);
};

export const getFileExtension = (fileName) => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

export const getFileExtensionLabel = (fileName) => getFileExtension(fileName).toUpperCase();

/** Split "photo.jpg" → { baseName: "photo", extension: ".jpg" } */
export const splitFileNameParts = (fileName) => {
  if (!fileName) return { baseName: '', extension: '' };
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot <= 0) return { baseName: fileName, extension: '' };
  return {
    baseName: fileName.slice(0, lastDot),
    extension: fileName.slice(lastDot),
  };
};

export const composeFileName = (baseName, extension) => {
  const base = (baseName || '').trim();
  if (!base) return '';
  return extension ? `${base}${extension}` : base;
};

/** Arrays (and other non-strings) become a single string. Axios would otherwise send
 *  originalName twice and Spring joins them with a comma: "abc,abc.txt". */
export const asScalarString = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) {
    const parts = value.map((part) => String(part ?? '').trim()).filter(Boolean);
    if (!parts.length) return '';
    return parts.reduce((longest, part) => (part.length > longest.length ? part : longest));
  }
  return String(value);
};

/**
 * Chrome/Safari concatenate download-attr + Content-Disposition with a comma
 * ("abc,abc.txt" or "abc.txt,abc.txt"). Collapse that back to one name.
 */
export const collapseDuplicatedFileName = (fileName) => {
  const raw = asScalarString(fileName).trim();
  if (!raw || !raw.includes(',')) return raw;
  const dup = raw.match(/^([^,]+),\1(\.[^.,\\/]+)?$/);
  if (dup) return `${dup[1]}${dup[2] || ''}`;
  return raw;
};

/**
 * Same comma-join bug on links: "https://abcd.com,abcd.com" or "https://a,https://b".
 */
export const collapseDuplicatedUrl = (url) => {
  let u = asScalarString(url).trim().replace(/&amp;/g, '&');
  if (!u) return '';
  if (/,https?:\/\//i.test(u)) {
    u = u.split(/,(?=https?:\/\/)/i)[0];
  }
  const comma = u.indexOf(',');
  if (comma <= 0) return u;
  const before = u.slice(0, comma);
  const after = u.slice(comma + 1).replace(/^\/+/, '').replace(/\/$/, '');
  try {
    const parsed = new URL(before);
    const host = parsed.hostname.replace(/^www\./i, '');
    const afterHost = after.replace(/^www\./i, '');
    if (afterHost === host || afterHost === parsed.host || after === parsed.hostname) {
      return before;
    }
  } catch {
    // keep original
  }
  return u;
};

/** Strip HTML so URLs can be found in rich message content. */
export const stripHtmlForTextExtraction = (html) =>
  (html || '')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/\s+/g, ' ')
    .trim();

/** SockJS/STOMP transport URLs must never be treated as user links. */
export const isInternalAppTransportUrl = (url) => {
  if (!url) return false;
  try {
    const parsed = new URL(String(url).trim().replace(/&amp;/g, '&'));
    const path = `${parsed.pathname || ''}${parsed.search || ''}`.toLowerCase();
    if (!path.includes('/ws')) return false;
    return /\/ws(?:\/|$)/.test(path)
      && /(xhr_send|xhr_streaming|eventsource|websocket|iframe\.html|\/info(?:\?|$))/.test(path);
  } catch {
    const lower = String(url).toLowerCase();
    return lower.includes('/ws/') && (
      lower.includes('xhr_send')
      || lower.includes('xhr_streaming')
      || lower.includes('eventsource')
      || lower.includes('websocket')
    );
  }
};

const normalizeExtractedUrl = (url) => {
  if (!url) return null;
  let u = collapseDuplicatedUrl(url);
  u = u.replace(/[.,;:!?)]+$/g, '');
  if (!u) return null;
  if (!/^https?:\/\//i.test(u) && !/^www\./i.test(u)) return null;
  if (!u.startsWith('http://') && !u.startsWith('https://')) {
    u = `https://${u}`;
  }
  if (isInternalAppTransportUrl(u)) return null;
  return u;
};

/** Join URL segments split by whitespace (e.g. "MERV -116" → "MERV-116"). */
const repairBrokenUrlWhitespace = (text) => {
  if (!text) return text;
  let repaired = text;
  let prev;
  do {
    prev = repaired;
    repaired = repaired.replace(/(https?:\/\/[^\s<>"']+?)\s+(-[^\s<>"']+)/gi, '$1$2');
  } while (repaired !== prev);
  return repaired;
};

/** First http(s) or www URL in plain or HTML message text (prefers the longest match). */
export const extractUrlFromText = (text) => {
  if (!text) return null;
  const decoded = String(text).replace(/&amp;/g, '&');
  const candidates = new Set();

  const addCandidate = (raw) => {
    const normalized = normalizeExtractedUrl(raw);
    if (normalized) candidates.add(normalized);
  };

  // Quill may put only the origin in href with the path as visible link text
  const anchorRegex = /<a\b[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;
  let anchorMatch;
  while ((anchorMatch = anchorRegex.exec(decoded)) !== null) {
    const href = anchorMatch[1].trim();
    const innerPlain = stripHtmlForTextExtraction(anchorMatch[2]);
    addCandidate(href);
    if (/^https?:\/\//i.test(href) && innerPlain && !/^https?:\/\//i.test(innerPlain)) {
      const trimmedInner = innerPlain.trim();
      // Quill sometimes puts only the origin in href and the real path as link text
      // (e.g. href="https://site.com" text="/app/page"). Do not append labels like "WORKLOOP APP".
      let hrefIsOriginOnly = false;
      try {
        const parsedHref = new URL(href);
        hrefIsOriginOnly = (parsedHref.pathname === '/' || parsedHref.pathname === '')
          && !parsedHref.search
          && !parsedHref.hash;
      } catch {
        hrefIsOriginOnly = false;
      }
      const innerLooksLikePath = /^[/?#]/.test(trimmedInner)
        || /^[a-zA-Z0-9._~-]+(?:\/[a-zA-Z0-9._~-]+)+$/.test(trimmedInner.replace(/\s+/g, ''));
      if (hrefIsOriginOnly && innerLooksLikePath) {
        const pathPart = trimmedInner.replace(/\s+/g, '');
        const base = href.replace(/\/+$/, '');
        const path = pathPart.startsWith('/') || pathPart.startsWith('?') || pathPart.startsWith('#')
          ? pathPart
          : `/${pathPart}`;
        addCandidate(`${base}${path}`);
      }
    }
  }

  const hrefRegex = /href\s*=\s*["']([^"']+)["']/gi;
  let hrefMatch;
  while ((hrefMatch = hrefRegex.exec(decoded)) !== null) {
    addCandidate(hrefMatch[1]);
  }

  const rawRegex = /(https?:\/\/[^\s<>"']+)/gi;
  let rawMatch;
  while ((rawMatch = rawRegex.exec(decoded)) !== null) {
    addCandidate(rawMatch[1]);
  }

  const plain = repairBrokenUrlWhitespace(stripHtmlForTextExtraction(decoded));
  const plainRegex = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;
  let plainMatch;
  while ((plainMatch = plainRegex.exec(plain)) !== null) {
    addCandidate(plainMatch[0]);
  }

  if (candidates.size === 0) return null;
  return [...candidates].sort((a, b) => b.length - a.length)[0];
};

/**
 * Decode HTML that was entity-escaped (e.g. legacy call notes stored as &lt;p&gt;...).
 * Safe to call on normal Quill HTML — leaves it unchanged when no entities are present.
 */
export const normalizeMessageContentHtml = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  const trimmed = html.trim();
  if (!trimmed) return '';

  if (
    !trimmed.includes('&lt;')
    && !trimmed.includes('&gt;')
    && !trimmed.includes('&amp;')
    && !trimmed.includes('&#')
  ) {
    return trimmed;
  }

  if (typeof document === 'undefined') {
    return trimmed
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&amp;/g, '&')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = trimmed;
  return (textarea.value || trimmed).trim();
};

export const isRichTextMessageContent = (content) => {
  const normalized = unwrapEscapedHtmlContent(content);
  return /<[a-z][\s\S]*>/i.test(normalized);
};

/**
 * Quill stores unsupported tags as text (&lt;div...&gt; inside a &lt;p&gt;).
 * Decode until the payload is real HTML so chat can render the look and feel.
 */
export const unwrapEscapedHtmlContent = (html) => {
  if (!html || typeof html !== 'string') return html || '';
  let current = normalizeMessageContentHtml(html);
  for (let i = 0; i < 4; i += 1) {
    const trimmed = current.trim();
    if (!trimmed) return '';
    if (typeof document !== 'undefined') {
      const el = document.createElement('div');
      el.innerHTML = current;
      const text = String(el.textContent || '').replace(/\u00a0/g, ' ').trim();
      if (text.startsWith('<') && /<\/[a-z][a-z0-9]*>/i.test(text)) {
        current = normalizeMessageContentHtml(text);
        continue;
      }
    }
    if (trimmed.includes('&lt;') && trimmed.includes('&gt;')) {
      current = normalizeMessageContentHtml(trimmed);
      continue;
    }
    break;
  }
  return current;
};

/** Fix truncated Quill anchor hrefs using the resolved full URL.
 *  Only rewrites anchors whose href is clearly a truncated form of preferredUrl
 *  (origin-only or a strict prefix). Never clobber unrelated same-origin links.
 */
export const fixMessageLinkHtml = (html, preferredUrl) => {
  const url = preferredUrl || extractUrlFromText(html);
  if (!html || !url || isInternalAppTransportUrl(url)) return html;
  const href = decodeLinkHref(url);
  try {
    const target = new URL(href);
    const targetHref = href.replace(/\/$/, '');
    return html.replace(
      /<a\b([^>]*?)\bhref\s*=\s*["']([^"']*)["']/gi,
      (match, attrs, hrefValue) => {
        try {
          const decoded = decodeLinkHref(hrefValue);
          if (!decoded || isInternalAppTransportUrl(decoded)) {
            const safeHref = href.replace(/"/g, '&quot;');
            return `<a${attrs}href="${safeHref}"`;
          }
          const linkUrl = new URL(decoded, target.origin);
          if (linkUrl.origin !== target.origin) {
            return match;
          }
          const currentHref = linkUrl.href.replace(/\/$/, '');
          const isOriginOnly = linkUrl.pathname === '/' && !linkUrl.search && !linkUrl.hash;
          const isTruncatedPrefix = targetHref.startsWith(currentHref)
            && targetHref.length > currentHref.length;
          // Only repair origin-only Quill hrefs. Never append visible link text onto a full href.
          if (isOriginOnly && (isTruncatedPrefix || isInternalAppTransportUrl(decoded))) {
            const safeHref = href.replace(/"/g, '&quot;');
            return `<a${attrs}href="${safeHref}"`;
          }
        } catch {
          // keep original anchor
        }
        return match;
      },
    );
  } catch {
    return html;
  }
};

export const linkDomainFromUrl = (url) => {
  try {
    return new URL(url).hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
};

/** Normalize URL for deduplicating root-level shared links. */
export const normalizeLinkUrlForDedup = (url) => {
  if (!url) return '';
  const decoded = String(url).replace(/&amp;/g, '&').trim();
  try {
    const withProtocol = decoded.startsWith('http://') || decoded.startsWith('https://')
      ? decoded
      : `https://${decoded}`;
    const parsed = new URL(withProtocol);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
    const path = parsed.pathname.replace(/\/$/, '') || '';
    const search = parsed.search || '';
    return `${parsed.protocol}//${host}${path}${search}`.toLowerCase();
  } catch {
    return decoded.toLowerCase().replace(/\/$/, '');
  }
};

export const decodeLinkHref = (url) => collapseDuplicatedUrl(url);

/** Display title for a link preview or shared-resources link entry. */
export const getLinkDisplayTitle = (link) => {
  if (!link) return '';
  const custom = (link.customTitle || '').trim();
  if (custom) return custom;
  const title = (link.title || '').trim();
  if (title) return title;
  return (link.url || '').trim();
};

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif']);

export const isImageFileName = (fileName) => {
  const ext = getFileExtension(fileName);
  return ext ? IMAGE_EXTENSIONS.has(ext) : false;
};

export const isDisplayableImage = (attachment) => {
  if (!attachment) return false;
  const ext = getFileExtension(attachment.fileName);
  if (ext === 'svg') return false;

  const fileType = (attachment.fileType || '').toUpperCase();
  if (fileType === 'IMAGE') return true;

  return isImageFileName(attachment.fileName);
};

export const isLinkAttachment = (attachment) =>
  (attachment?.fileType || '').toUpperCase() === 'LINK';

export const canGroupSharedAttachments = (attachments = []) =>
  Array.isArray(attachments) && attachments.length >= 2;

export const getImageAttachments = (attachments = []) =>
  attachments.filter(isDisplayableImage);

/** Document / readable file extensions for Shared Files "Files" filter. */
export const DOCUMENT_EXTENSIONS = new Set([
  'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx',
  'txt', 'md', 'csv', 'rtf', 'odt', 'ods', 'odp',
]);

export const SHARED_FILE_FILTER_KEYS = ['images', 'files', 'links', 'others', 'groups', 'liked'];

export const createDefaultSharedFileFilters = () =>
  SHARED_FILE_FILTER_KEYS.reduce((acc, key) => ({ ...acc, [key]: true }), {});

export const getSharedEntryCategory = (entry) => {
  if (!entry) return 'others';
  if (entry.type === 'link') return 'links';
  if (entry.type === 'group') return 'groups';

  const ext = getFileExtension(entry.fileName);
  const fileType = (entry.fileType || '').toUpperCase();

  if (isDisplayableImage(entry)) return 'images';
  if (DOCUMENT_EXTENSIONS.has(ext)) return 'files';
  if (fileType === 'VIDEO' || fileType === 'AUDIO') return 'others';
  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'others';

  return 'others';
};

/** Whether this shared entry belongs in the Liked photos filter for `userId`. */
export const sharedEntryMatchesLikedFilter = (entry, userId) => {
  if (!entry || !userId) return false;
  const likedBy = (attachment) => {
    const ids = Array.isArray(attachment?.likedByUserIds) ? attachment.likedByUserIds : [];
    return ids.some((id) => String(id) === String(userId));
  };
  if (entry.type === 'group') {
    return (entry.attachments || []).some(
      (att) => isDisplayableImage(att) && likedBy(att)
    );
  }
  if (entry.type === 'link') return false;
  return isDisplayableImage(entry) && likedBy(entry);
};

/** Hover preview kind for shared files / attachments. */
export const getFilePreviewKind = (attachment) => {
  if (!attachment) return 'generic';
  if (isDisplayableImage(attachment)) return 'image';
  const ext = getFileExtension(attachment.fileName);
  if (ext === 'pdf') return 'pdf';
  return 'generic';
};

const TEXT_SLIDER_PREVIEW_EXTENSIONS = new Set([
  'txt', 'md', 'markdown', 'csv', 'tsv', 'log', 'json', 'xml', 'yaml', 'yml',
  'rtf', 'html', 'htm', 'css', 'scss', 'js', 'jsx', 'ts', 'tsx', 'sql', 'sh',
  'bash', 'py', 'java', 'go', 'rb', 'php', 'ini', 'conf', 'toml', 'env',
  'properties', 'svg', 'ndjson',
]);

const DOCX_PREVIEW_EXTENSIONS = new Set(['docx']);

/** Preview mode for Shared Resources group viewer slider. */
export const getSliderPreviewKind = (attachment) => {
  if (!attachment || isLinkAttachment(attachment)) return 'none';
  if (isDisplayableImage(attachment)) return 'image';
  const ext = getFileExtension(attachment.fileName);
  if (ext === 'pdf') return 'pdf';
  if (DOCX_PREVIEW_EXTENSIONS.has(ext)) return 'docx';
  if (TEXT_SLIDER_PREVIEW_EXTENSIONS.has(ext)) return 'text';
  return 'none';
};

export const canPreviewInApp = (attachment) => {
  const kind = getSliderPreviewKind(attachment);
  return kind === 'image' || kind === 'pdf' || kind === 'text' || kind === 'docx';
};

/** Extract readable text from a .docx (Office Open XML) ArrayBuffer. */
export const extractDocxPlainText = async (arrayBuffer) => {
  const zip = await JSZip.loadAsync(arrayBuffer);
  const xml = await zip.file('word/document.xml')?.async('string');
  if (!xml) {
    throw new Error('Could not read Word document');
  }
  return xml
    .replace(/<w:tab\b[^/]*\/>/g, '\t')
    .replace(/<w:br\b[^/]*\/>/g, '\n')
    .replace(/<\/w:p>/g, '\n')
    .replace(/<w:t\b[^>]*>/g, '')
    .replace(/<\/w:t>/g, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

export const getFileIconKind = (extension) => {
  const ext = (extension || '').toLowerCase().replace(/^\./, '');
  if (!ext) return 'file';
  if (ext === 'pdf') return 'pdf';
  if (['doc', 'docx', 'rtf', 'odt'].includes(ext)) return 'doc';
  if (['xls', 'xlsx', 'csv', 'ods', 'tsv'].includes(ext)) return 'sheet';
  if (['ppt', 'pptx', 'odp', 'key'].includes(ext)) return 'slides';
  if (['txt', 'md', 'markdown', 'log'].includes(ext)) return 'text';
  if (['zip', 'rar', '7z', 'tar', 'gz', 'tgz', 'bz2'].includes(ext)) return 'archive';
  if (['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'opus', 'wma'].includes(ext)) return 'audio';
  if (['mp4', 'avi', 'mov', 'webm', 'mkv', 'm4v', 'wmv'].includes(ext)) return 'video';
  if ([
    'json', 'xml', 'yaml', 'yml', 'js', 'jsx', 'ts', 'tsx', 'py', 'java', 'go', 'rb',
    'php', 'html', 'htm', 'css', 'scss', 'sql', 'sh', 'bash', 'c', 'cpp', 'h', 'rs',
  ].includes(ext)) return 'code';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'heif', 'svg'].includes(ext)) return 'image';
  return 'file';
};

/** True when the URL already carries a download filename (S3 presign or legacy API). */
export const downloadUrlSetsFilename = (fileUrl) => {
  if (!fileUrl) return false;
  try {
    const decoded = decodeURIComponent(fileUrl);
    if (/response-content-disposition=[^&]*filename/i.test(decoded)) {
      return true;
    }
    const u = new URL(fileUrl);
    if (u.searchParams.get('download') === 'true' && u.searchParams.has('originalName')) {
      return true;
    }
  } catch (_) {
    if (/response-content-disposition=[^&]*filename/i.test(fileUrl)) {
      return true;
    }
  }
  return false;
};

export const downloadAttachment = async (attachment) => {
  const fileName = collapseDuplicatedFileName(attachment?.fileName) || 'download';
  if (isInlineFileSrc(attachment?.fileUrl)) {
    const link = document.createElement('a');
    link.href = attachment.fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }
  if (isLocalAttachmentKey(attachment?.fileUrl) || isLocalAttachmentKey(attachment?.localFileId)) {
    const stored = await readLocalFile(attachment.localFileId || attachment.fileUrl);
    if (!stored?.blob) throw new Error('Offline file is missing');
    const objectUrl = URL.createObjectURL(stored.blob);
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName || stored.name || 'download';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
    return;
  }
  const fileUrl = await getPresignedFileUrl(attachment.fileUrl, {
    download: true,
    originalName: fileName,
  });
  if (!fileUrl) throw new Error('No URL');
  const link = document.createElement('a');
  link.href = fileUrl;
  // Never set download on remote URLs: Chrome/Safari join it with Content-Disposition
  // as "abc,abc.txt". blob:/data: URLs above still use download=.
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const openAttachmentFallback = (attachment) => {
  const openUrl = buildLegacyFileUrl(attachment.fileUrl, {
    download: true,
    originalName: collapseDuplicatedFileName(attachment?.fileName),
  });
  if (openUrl) window.open(openUrl, '_blank');
};

const sanitizeZipBaseName = (name) => {
  const trimmed = (name || '').trim().replace(/[<>:"/\\|?*\x00-\x1F]/g, '_').replace(/\.+$/g, '');
  return trimmed || 'attachments';
};

const uniqueZipEntryName = (fileName, usedNames, index) => {
  const base = fileName?.trim() || `file-${index + 1}`;
  if (!usedNames.has(base)) {
    usedNames.add(base);
    return base;
  }
  const dot = base.lastIndexOf('.');
  const stem = dot > 0 ? base.slice(0, dot) : base;
  const ext = dot > 0 ? base.slice(dot) : '';
  let n = 2;
  let candidate = `${stem} (${n})${ext}`;
  while (usedNames.has(candidate)) {
    n += 1;
    candidate = `${stem} (${n})${ext}`;
  }
  usedNames.add(candidate);
  return candidate;
};

async function fetchAttachmentBlob(attachment) {
  if (attachment?.file instanceof Blob) {
    return attachment.file;
  }

  if (!attachment?.fileUrl && !attachment?.localFileId) {
    throw new Error('Missing file URL');
  }

  if (isInlineFileSrc(attachment.fileUrl)) {
    const response = await fetch(attachment.fileUrl);
    if (!response.ok) throw new Error(`Failed to download ${attachment?.fileName || 'file'}`);
    return response.blob();
  }

  if (isLocalAttachmentKey(attachment.fileUrl) || isLocalAttachmentKey(attachment.localFileId)) {
    const stored = await readLocalFile(attachment.localFileId || attachment.fileUrl);
    if (!stored?.blob) {
      throw new Error(`Could not resolve URL for ${attachment?.fileName || 'file'}`);
    }
    return stored.blob;
  }

  const fetchOptions = { originalName: attachment?.fileName };
  const apiUrl = buildLegacyFileUrl(attachment.fileUrl, {
    ...fetchOptions,
    includeToken: false,
  });
  if (!apiUrl) {
    throw new Error(`Could not resolve URL for ${attachment?.fileName || 'file'}`);
  }

  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

  const tryFetch = async (url, headers = {}) => {
    const response = await fetch(url, { headers });
    if (!response.ok) {
      throw new Error(`Failed to download ${attachment?.fileName || 'file'}: HTTP ${response.status}`);
    }
    const blob = await response.blob();
    if (!blob || blob.size === 0) {
      throw new Error(`Failed to download ${attachment?.fileName || 'file'}`);
    }
    const contentType = String(blob.type || '').toLowerCase();
    const isJsonAttachment = /\.json$/i.test(attachment?.fileName || '')
      || String(attachment?.fileType || '').toUpperCase() === 'JSON';
    // Auth/API errors are JSON, but user-uploaded .json files are too.
    if (contentType.includes('application/json') && !isJsonAttachment) {
      let detail = '';
      try {
        const text = await blob.text();
        try {
          const parsed = JSON.parse(text);
          detail = parsed?.error || parsed?.message || text;
        } catch (_) {
          detail = text;
        }
      } catch (_) {
        // ignore
      }
      throw new Error(detail || `Failed to download ${attachment?.fileName || 'file'}`);
    }
    return blob;
  };

  try {
    return await tryFetch(apiUrl, authHeaders);
  } catch (err) {
    if (!token) throw err;
    const authedUrl = buildLegacyFileUrl(attachment.fileUrl, fetchOptions);
    if (!authedUrl || authedUrl === apiUrl) throw err;
    return tryFetch(authedUrl);
  }
}

export const loadAttachmentArrayBuffer = async (attachment) => {
  const blob = await fetchAttachmentBlob(attachment);
  return blob.arrayBuffer();
};

/** Download multiple attachments as a single ZIP archive. */
export async function downloadAttachmentsAsZip(attachments = [], zipBaseName = '') {
  const files = (attachments || []).filter((att) => att?.fileUrl && !isLinkAttachment(att));
  if (!files.length) {
    throw new Error('No files to download');
  }

  const zip = new JSZip();
  const usedNames = new Set();
  const downloadName = sanitizeZipBaseName(zipBaseName);

  for (let i = 0; i < files.length; i++) {
    const attachment = files[i];
    const blob = await fetchAttachmentBlob(attachment);
    const entryName = uniqueZipEntryName(attachment.fileName, usedNames, i);
    zip.file(entryName, blob);
  }

  const zipBlob = await zip.generateAsync({ type: 'blob' });
  const objectUrl = URL.createObjectURL(zipBlob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = `${downloadName}.zip`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}
